import React, { useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import {
  Box,
  Button,
  FormControl,
  Input,
  Textarea,
  useToast,
  VStack,
  Heading,
  Flex,
  Icon,
  Text,
  Link,
  useColorModeValue,
} from "@chakra-ui/react";
import { FaLinkedin, FaGithub } from "react-icons/fa";
import { COLORS } from "../../constants/COLORS";
import "./styles.css";
import { SiMinutemailer } from "react-icons/si";
import { ReactTyped } from "react-typed";

declare global {
  interface Window {
    dataLayer?: { push: (event: any) => void };
  }
}

export const Contact = () => {
  const form = useRef<HTMLFormElement>(null);
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const [formError, setFormError] = useState(false);

  const hoverColor = useColorModeValue(COLORS.GRAY, COLORS.WHITE);
  const textColor = useColorModeValue(COLORS.DARK_BLUE, COLORS.WHITE_SMOKE);
  const successBorderColor = useColorModeValue(COLORS.GRAY, COLORS.WHITE_SMOKE);
  const errorBorderColor = useColorModeValue("red.300", "red.400");
  const buttonBorderColor = useColorModeValue(COLORS.WHITE_SMOKE, COLORS.GRAY);
  const buttonHoverBg = useColorModeValue(COLORS.DARK_BLUE, COLORS.WHITE_SMOKE);
  const buttonHoverColor = useColorModeValue(
    COLORS.WHITE_SMOKE,
    COLORS.DARK_BLUE,
  );

  const sendEmail = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!form.current || messageSent || formError) return;

    setLoading(true);
    setIsAnimating(true);

    try {
      await emailjs.sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID as string,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID as string,
        form.current,
        process.env.REACT_APP_EMAILJS_USER_ID as string,
      );

      setMessageSent(true);

      toast({
        title: "Thank you for reaching out!",
        description:
          "Your message has been received. I will get back to you as soon as possible.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      window.dataLayer?.push({
        event: "form_submitted",
      });

      form.current.reset();
    } catch (error: any) {
      console.error("Error sending the message:", error);

      setFormError(true);

      window.dataLayer?.push({
        event: "form_submit_error",
        form_name: "contact_form",
        error_source: "emailjs",
        error_code: error?.status || "unknown",
        error_message: error?.text || error?.message || "unknown",
      });

      toast({
        title: "Message could not be sent.",
        description: "Please use the alternative contact options shown below.",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    }
  };

  return (
    <Flex
      flexWrap="wrap"
      justifyContent="space-between"
      fontFamily="Poppins"
      id="contact"
      ml={0}
      flexDirection={{ base: "column", md: "row" }}
      mt={3}
      w="full"
    >
      <Box flex="2" borderRadius="md" mr={{ base: 0, md: 8 }} w="full" minW={0}>
        <Heading as="h2" size="lg" mb={6} fontFamily="Poppins">
          <ReactTyped strings={["Get in Touch"]} typeSpeed={90} />
        </Heading>

        <Text mb={2}>
          <b className="contact-info">Email:</b>{" "}
          <a
            href="mailto:contact@joao-silva.pt"
            onClick={() => {
              window.dataLayer?.push({
                event: "cta_click",
                cta_name: "contact@joao-silva.pt",
              });
            }}
          >
            contact@joao-silva.pt
          </a>
        </Text>

        <Text mb={2}>
          <b className="contact-info">Phone:</b>
          <a
            href="tel:+351925492565"
            onClick={() => {
              window.dataLayer?.push({
                event: "cta_click",
                cta_name: "+351 925 492 565",
              });
            }}
          >
            +351 925 492 565
          </a>
        </Text>

        <Text mb={6}>
          <b className="contact-info">Location:</b> Lisbon, Portugal
        </Text>

        <Flex mt={4} mb={4}>
          <Link
            mr={5}
            href="https://github.com/joaovff"
            target="_blank"
            onClick={() => {
              window.dataLayer?.push({
                event: "cta_click",
                cta_name: "github icon",
              });
            }}
          >
            <Icon
              as={FaGithub}
              boxSize={6}
              cursor="pointer"
              _hover={{ color: hoverColor }}
            />
          </Link>

          <Link
            mr={5}
            href="https://www.linkedin.com/in/joao-figueiredo-silva"
            target="_blank"
            onClick={() => {
              window.dataLayer?.push({
                event: "cta_click",
                cta_name: "linkedin icon",
              });
            }}
          >
            <Icon
              as={FaLinkedin}
              boxSize={6}
              cursor="pointer"
              _hover={{ color: hoverColor }}
            />
          </Link>
        </Flex>
      </Box>

      <Box flex="1.5" borderRadius="md" mb={5} w="full" minW={0}>
        <Text
          className="chakra-text subtitle css-0"
          mb={4}
          style={{ textAlign: "left" }}
        >
          Contact me for collaborations, inquiries, or just to say hello!
        </Text>

        {messageSent ? (
          <Box marginTop={"28px"} color={textColor}>
            <Text fontSize="lg" fontWeight="bold" mb={3} color={"#55af58"}>
              Thank you for your message!
            </Text>

            <Text fontWeight={300}>
              Your message has been successfully received. I will review it and
              get back to you as soon as possible.
            </Text>
          </Box>
        ) : formError ? (
          <Box mt={"28px"} color={textColor}>
            <Text fontSize="lg" fontWeight="bold" mb={3} color={"#ad4a3a"}>
              Oops, something went wrong.
            </Text>

            <Text mb={4}>
              It looks like there is a temporary issue with the contact form. An
              automatic notification has been triggered so I can review and fix
              the problem as soon as possible.
            </Text>

            <Text mb={4}>
              In the meantime, please feel free to contact me directly through
              one of the following options:
            </Text>

            <Text mb={2}>
              <b>Email:</b>{" "}
              <a href="mailto:contact@joao-silva.pt">contact@joao-silva.pt</a>
            </Text>

            <Text>
              <b>Phone:</b> <a href="tel:+351925492565">+351 925 492 565</a>
            </Text>
          </Box>
        ) : (
          <form ref={form} onSubmit={sendEmail}>
            <VStack spacing={1} align="stretch">
              <FormControl mb={4}>
                <Input
                  type="text"
                  name="user_name"
                  placeholder="e.g. Tony Stark"
                  required
                  focusBorderColor={textColor}
                  variant="outline"
                />
              </FormControl>

              <FormControl mb={4}>
                <Input
                  type="email"
                  name="user_email"
                  placeholder="e.g. your.email@example.com"
                  required
                  focusBorderColor={textColor}
                  variant="outline"
                />
              </FormControl>

              <FormControl mb={4}>
                <Textarea
                  name="message"
                  placeholder="Hi, I'd like to talk about..."
                  required
                  focusBorderColor={textColor}
                  variant="outline"
                />
              </FormControl>

              <Button
                onClick={() => {
                  window.dataLayer?.push({
                    event: "cta_click",
                    cta_name: "submit contact form",
                  });
                }}
                type="submit"
                isLoading={loading}
                loadingText="Sending..."
                isDisabled={loading || messageSent || formError}
                size="lg"
                mt={4}
                variant="outline"
                color={textColor}
                borderColor={buttonBorderColor}
                backgroundColor="transparent"
                _hover={{
                  background: buttonHoverBg,
                  color: buttonHoverColor,
                }}
                _disabled={{
                  cursor: "not-allowed",
                  opacity: 0.5,
                  backgroundColor: "gray.400",
                  color: "gray.700",
                  borderColor: "gray.400",
                }}
              >
                <SiMinutemailer
                  className={isAnimating ? "send-animation" : ""}
                  style={{ marginRight: 15 }}
                />
                Send
              </Button>
            </VStack>
          </form>
        )}
      </Box>
    </Flex>
  );
};
